import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { pharmacyApi } from "@/api/pharmacy";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function OTReconciliationPage() {
  const qc = useQueryClient();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [physical, setPhysical] = useState<Record<string, string>>({});
  const [reason, setReason] = useState<Record<string, string>>({});

  const { data } = useQuery({
    queryKey: ["ot-dashboard"],
    queryFn: async () => {
      const res = await pharmacyApi.otDashboard();
      return (res.data as any)?.data;
    },
  });

  const rows = useMemo(() => data?.items ?? [], [data]);

  const reconcileMutation = useMutation({
    mutationFn: async () => {
      if (!date) throw new Error("Select date");
      if (rows.length === 0) throw new Error("No OT items (configure min-stock first)");
      return pharmacyApi.reconcileOT({
        date,
        notes: notes.trim() || undefined,
        lines: rows.map((r: any) => ({
          drugId: r.drug.id,
          physicalQty: Math.max(0, parseInt(physical[r.drug.id] ?? String(r.currentQty ?? 0), 10) || 0),
          reason: (reason[r.drug.id] ?? "").trim() || undefined,
        })),
      });
    },
    onSuccess: async () => {
      toast.success("Reconciliation saved (system stock adjusted)");
      setNotes("");
      await qc.invalidateQueries({ queryKey: ["ot-dashboard"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>OT Reconciliation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Date *</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Physical stock entry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-2">Drug</th>
                  <th className="text-right p-2">Expected</th>
                  <th className="text-right p-2">Physical</th>
                  <th className="text-left p-2">Reason (if variance)</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={4} className="p-3 text-muted-foreground">No items. Configure OT min-stock first.</td></tr>
                ) : (
                  rows.map((r: any) => (
                    <tr key={r.drug.id} className="border-t">
                      <td className="p-2">{r.drug.name} {r.drug.strength ? `(${r.drug.strength})` : ""}</td>
                      <td className="p-2 text-right">{r.currentQty}</td>
                      <td className="p-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          value={physical[r.drug.id] ?? String(r.currentQty ?? 0)}
                          onChange={(e) => setPhysical((p) => ({ ...p, [r.drug.id]: e.target.value }))}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={reason[r.drug.id] ?? ""}
                          onChange={(e) => setReason((p) => ({ ...p, [r.drug.id]: e.target.value }))}
                          placeholder="Optional"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end pt-3">
            <Button onClick={() => reconcileMutation.mutate()} disabled={reconcileMutation.isPending}>
              {reconcileMutation.isPending ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Saving...</span> : "Save reconciliation"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

